import { useCallback, useState } from 'react';
import type { FileRejection } from 'react-dropzone';

export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

export type FileRow = {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'failed';
};

type Params = {
  vendorName: string;
  acceptsCopy: string;
  onUploadFile: (file: File) => Promise<void>;
  onProcess: () => Promise<void>;
};

export function useInvoiceUpload({ vendorName, acceptsCopy, onUploadFile, onProcess }: Params) {
  const [rows, setRows] = useState<FileRow[]>([]);
  const [dropError, setDropError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      const oversized = rejected.find((r) => r.file.size > MAX_FILE_SIZE_BYTES);
      if (oversized) {
        setDropError(`${oversized.file.name} is too large — max 25 MB per file.`);

        return;
      }

      if (rejected.length > 0) {
        setDropError(
          `${rejected[0].file.name} isn't an ${vendorName} format — try ${acceptsCopy}.`
        );

        return;
      }

      setDropError(null);
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r.file.name));
        const additions = accepted
          .filter((f) => !seen.has(f.name))
          .map<FileRow>((file) => ({ id: crypto.randomUUID(), file, status: 'pending' }));

        return [...prev, ...additions];
      });
    },
    [vendorName, acceptsCopy]
  );

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setDropError(null);
  }, []);

  const runProcess = useCallback(async () => {
    setProcessing(true);
    try {
      await onProcess();
      setProcessError(null);
    } catch (e) {
      setProcessError(e instanceof Error ? e.message : String(e));
    } finally {
      setProcessing(false);
    }
  }, [onProcess]);

  const handleProcessClick = useCallback(async () => {
    const batch = rows.filter((r) => r.status !== 'done');
    if (batch.length === 0) {
      await runProcess();

      return;
    }

    const batchIds = new Set(batch.map((r) => r.id));
    setRows((prev) => prev.map((r) => (batchIds.has(r.id) ? { ...r, status: 'uploading' } : r)));

    const results = await Promise.all(
      batch.map(async (row) => {
        try {
          await onUploadFile(row.file);

          return { id: row.id, ok: true };
        } catch {
          return { id: row.id, ok: false };
        }
      })
    );
    const byId = new Map(results.map((res) => [res.id, res]));

    setRows((prev) =>
      prev.map((r) => {
        const res = byId.get(r.id);
        if (!res) {
          return r;
        }

        return { ...r, status: res.ok ? 'done' : 'failed' };
      })
    );

    if (results.every((res) => res.ok)) {
      await runProcess();
    }
  }, [rows, onUploadFile, runProcess]);

  const handleRetry = useCallback(
    async (id: string) => {
      const target = rows.find((r) => r.id === id);
      if (!target) {
        return;
      }

      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'uploading' } : r)));

      let ok = false;
      try {
        await onUploadFile(target.file);
        ok = true;
      } catch {
        // surfaced as the row-level "Upload failed — please retry" message
      }

      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: ok ? 'done' : 'failed' } : r))
      );

      if (!ok) {
        return;
      }

      // rows is the closure snapshot from before the retry: every *other* row was
      // either already 'done' or 'failed' when the handler started. If all others
      // are 'done' and this retry just succeeded, all rows are now done.
      const allDone = rows.every((r) => r.id === id || r.status === 'done');
      if (allDone) {
        await runProcess();
      }
    },
    [rows, onUploadFile, runProcess]
  );

  return {
    rows,
    dropError,
    processing,
    processError,
    onDrop,
    removeRow,
    runProcess,
    handleProcessClick,
    handleRetry,
  };
}
